import { type ReactNode, useMemo, useCallback } from 'react';
import { useAuthStore } from '../../stores/auth.store';
import { checkPermission, type PermissionKey } from '../../lib/permissions';

export type { PermissionKey } from '../../lib/permissions';

interface RequirePermissionProps {
  permission: PermissionKey;
  children: ReactNode;
  fallback?: ReactNode;
}

export function RequirePermission({ permission, children, fallback = null }: RequirePermissionProps) {
  const profile = useAuthStore((s) => s.profile);
  const isOwner = useAuthStore((s) => s.isOwner);

  const effectiveProfile = useMemo(
    () => (profile ? { ...profile, is_owner: profile.is_owner || isOwner } : null),
    [profile, isOwner]
  );

  if (checkPermission(effectiveProfile, permission)) {
    return <>{children}</>;
  }
  return <>{fallback}</>;
}

/**
 * Hook imperativo memoizado para verificar permisos
 */
export function useHasPermission() {
  const profile = useAuthStore((s) => s.profile);
  const isOwner = useAuthStore((s) => s.isOwner);

  const effectiveProfile = useMemo(
    () => (profile ? { ...profile, is_owner: profile.is_owner || isOwner } : null),
    [profile, isOwner]
  );

  return useCallback(
    (permission: PermissionKey) => checkPermission(effectiveProfile, permission),
    [effectiveProfile]
  );
}
